/**
 * Pricing-context resolver — port of the v2.9 monkey-patch wrapper around
 * calculateCommission_ (docs/Payroll/PayrollAutomation_v2.gs), with one
 * deliberate v3.1.0 divergence (D-50, Marilyn ruling 2026-09-02):
 * LP vs BP is decided by the revenue ultimately earned vs the expected
 * margin ONLY — comp-code tags NEVER decide pricing (they are flag-only).
 * v2.9 still let a BP tag force BP when the lender had no margin row.
 *
 * Pre-computes, once per row, the shared determinations the split-rule
 * matcher and the main calc both depend on:
 *   - correspondent status
 *   - margin ROW (Investor column governs on correspondent loans)
 *   - margin COLUMN (W2 → Davidson, 1099 → Jax; keyed to the PRIMARY LO)
 *   - LP vs BP (revenue vs expected margin; $300 drift threshold)
 *
 * Mutates ctx: sets _isCorr, _margin, _marginName, _marginPctNum, _useBP,
 * _bpReason and may push MARGIN_COLUMN_MISSING / BP_TAG_OVERRIDDEN_BY_MARGIN
 * / BP_TAG_IGNORED_NO_MARGIN flags (it runs even for rows the calc later
 * skips: house accounts, missing profiles, missing core data).
 *
 * Calculator-only: options.pricingOverride ('LP'|'BP') forces the LP/BP
 * outcome AFTER the natural determination (batch runs never pass options).
 * Pure module — shared with the frontend bundle.
 */

const { TAGS } = require('./tags');
const { isW2 } = require('./numeric');

function resolvePricingContext(ctx, snapshot, options = {}) {
  const config = snapshot.config || {};
  const marginTable = snapshot.margin || {};
  const marginAliases = snapshot.marginAliases || {};
  const driftThreshold = config.bp_drift_threshold === undefined ? 300 : config.bp_drift_threshold;

  const lookupMargin = (name) => {
    if (!name) return undefined;
    if (marginTable[name] !== undefined) return { row: marginTable[name], name };
    const canonical = marginAliases[name];
    if (canonical && marginTable[canonical] !== undefined) {
      return { row: marginTable[canonical], name: canonical };
    }
    return undefined;
  };

  const isCorr = ctx.corrCol === 'correspondent' ||
    ctx.lender === 'future home loans' ||
    (!ctx.corrCol && (TAGS.CORRESPONDENT.test(ctx.compCode) ||
      (ctx.minNumber !== '' && ctx.minNumber !== null && ctx.minNumber !== undefined)));

  // ROW: correspondent loans fund in-house (Lender = Future Home Loans); the
  // real pricing counterparty — and the margin row that decides LP vs BP — is
  // the INVESTOR column (e.g. UWM). Correspondent is never automatically BP.
  const lenderHit = lookupMargin(ctx.lender);
  let marginRow = lenderHit ? lenderHit.row : undefined;
  let rowName = lenderHit ? lenderHit.name : ctx.lender;
  if (isCorr && ctx.investor) {
    const invHit = lookupMargin(ctx.investor);
    if (invHit) { marginRow = invHit.row; rowName = invHit.name; }
  }

  // COLUMN: margins are kept separate by branch — W2 LOs price against the
  // Davidson column, 1099 LOs against Jax (keyed to the PRIMARY LO's status).
  const wantCol = isW2(ctx.prof) ? 'davidson' : 'jax';
  let marginVal = marginRow ? marginRow[wantCol] : undefined;
  let colLabel = wantCol === 'davidson' ? 'Davidson' : 'Jax';
  if (marginRow && (marginVal === undefined || marginVal === '')) {
    const other = wantCol === 'davidson' ? 'jax' : 'davidson';
    if (marginRow[other] !== undefined && marginRow[other] !== '') {
      marginVal = marginRow[other];
      colLabel = (other === 'davidson' ? 'Davidson' : 'Jax') + ' (fallback)';
      ctx.flags.push('MARGIN_COLUMN_MISSING:' + rowName + ':' + wantCol);
    }
  }
  const marginName = rowName + ' ' + colLabel;
  const marginNum = marginVal !== undefined ? parseFloat(marginVal) : NaN;
  const driftBP = !isNaN(marginNum) &&
    ctx.revenue < ctx.loan * (marginNum / 100) &&
    (ctx.loan * (marginNum / 100) - ctx.revenue) > driftThreshold;
  const bpTag = TAGS.EXPLICIT_BP.test(ctx.compCode) && !TAGS.EXPLICIT_LP.test(ctx.compCode);
  let useBP = false;
  let bpReason = '';
  if (String(marginVal).toUpperCase() === 'BP') { useBP = true; bpReason = 'Lender is BP-only'; }
  else if (TAGS.HELOC.test(ctx.product) || TAGS.HELOC.test(ctx.compCode)) { useBP = true; bpReason = 'HELOC product'; }
  else if (!isNaN(marginNum)) {
    // Margin data exists → the margin ALONE decides, no matter what the comp
    // code says: revenue within $300 of expected LP revenue (or higher) = LP;
    // more than $300 below expected = BP.
    if (driftBP) { useBP = true; bpReason = `Revenue lower than expected (${marginName} margin)`; }
    else if (bpTag) { ctx.flags.push('BP_TAG_OVERRIDDEN_BY_MARGIN'); }
  } else if (bpTag) {
    // D-50: no margin row → revenue cannot be verified, and comp-code tags
    // never decide pricing. Default LP, flag for review (the real fix is
    // adding the lender/investor to the Margin tab, then drift decides).
    ctx.flags.push('BP_TAG_IGNORED_NO_MARGIN');
  }

  ctx._pricingOverridden = false;
  if (options.pricingOverride === 'LP' && useBP) {
    useBP = false; bpReason = ''; ctx._pricingOverridden = true;
  } else if (options.pricingOverride === 'BP' && !useBP) {
    useBP = true; bpReason = 'Manual override'; ctx._pricingOverridden = true;
  }

  ctx._useBP = useBP;
  ctx._isCorr = isCorr;
  ctx._bpReason = bpReason;
  ctx._margin = marginRow;
  ctx._marginName = marginName;
  ctx._marginPctNum = marginNum;
  ctx._marginRowName = rowName;
  ctx._marginColLabel = colLabel;
  ctx._driftBP = driftBP;
  ctx._bpTag = bpTag;
  return ctx;
}

module.exports = { resolvePricingContext };
